import { create } from 'zustand';
import ServiceColetaConfig, { CERTIDOES } from '@/services/ServiceColetaConfig';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';

type Any = any;

export interface CertidaoConfig {
  certidao: string;
  enabled: boolean;
  periodicidade: string | null;
  diasAntesVenc: number | null;
  mesesRetroativos: number | null;
  diasRetroativos: number | null;
  ultimaColeta: string | null;
  proximaColeta: string | null;
}

const vazio = (certidao: string): CertidaoConfig => ({
  certidao,
  enabled: false,
  periodicidade: null,
  diasAntesVenc: null,
  mesesRetroativos: null,
  diasRetroativos: null,
  ultimaColeta: null,
  proximaColeta: null,
});

// mescla as linhas do servidor com as certidões cobertas (sempre renderiza todas)
const merge = (rows: Any[]): CertidaoConfig[] =>
  CERTIDOES.map((c) => rows.find((r) => r.certidao === c) ?? vazio(c));

interface State {
  rootCnpj: string | null;
  nome: string;
  configs: CertidaoConfig[];
  historico: Any[];
  carregando: boolean;
  salvando: boolean;
  coletandoAgora: string | null;
  abrir: (rootCnpj: string, nome: string) => Promise<void>;
  fechar: () => void;
  carregarHistorico: () => Promise<void>;
  salvar: (configs: CertidaoConfig[]) => Promise<boolean>;
  coletarAgora: (certidao: string) => Promise<void>;
}

export const useColetaConfigStore = create<State>((set, get) => ({
  rootCnpj: null,
  nome: '',
  configs: [],
  historico: [],
  carregando: false,
  salvando: false,
  coletandoAgora: null,

  async abrir(rootCnpj, nome) {
    set({ rootCnpj, nome, carregando: true, historico: [] });
    try {
      const { data } = await ServiceColetaConfig.obter(rootCnpj);
      set({ configs: merge(data?.configs ?? []) });
    } catch {
      notify.error('Erro ao carregar a configuração de coleta.');
      set({ configs: merge([]) });
    } finally {
      set({ carregando: false });
    }
  },

  fechar() {
    set({ rootCnpj: null, nome: '', configs: [], historico: [] });
  },

  async carregarHistorico() {
    const { rootCnpj } = get();
    if (!rootCnpj) return;
    try {
      const { data } = await ServiceColetaConfig.historico(rootCnpj);
      set({ historico: data?.items ?? [] });
    } catch {
      set({ historico: [] });
    }
  },

  async salvar(configs) {
    const { rootCnpj } = get();
    if (!rootCnpj) return false;
    // habilitar exige periodicidade (o trigger é a cadência)
    const semCadencia = configs.find((c) => c.enabled && !c.periodicidade);
    if (semCadencia) {
      notify.error('Selecione a periodicidade das certidões habilitadas.');
      return false;
    }
    set({ salvando: true });
    try {
      const { data } = await ServiceColetaConfig.salvar(rootCnpj, configs);
      set({ configs: merge(data?.configs ?? []) });
      notify.success('Configuração de coleta salva.');
      return true;
    } catch (err) {
      notify.error(mensagemErro(err, 'Erro ao salvar a configuração de coleta.'));
      return false;
    } finally {
      set({ salvando: false });
    }
  },

  async coletarAgora(certidao) {
    const { rootCnpj } = get();
    if (!rootCnpj) return;
    set({ coletandoAgora: certidao });
    try {
      await ServiceColetaConfig.coletarAgora(rootCnpj, certidao);
      notify.success(`Coleta de ${certidao} disparada.`);
    } catch (err) {
      notify.error(mensagemErro(err, `Erro ao coletar ${certidao} agora.`));
    } finally {
      set({ coletandoAgora: null });
    }
  },
}));
